import { Controller, useFieldArray, useForm } from "react-hook-form";
import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import type { AgeRange } from "@/core/types";
import { Button2 } from "@/ui2/atoms/Button";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import PlusIcon from "@/assets/images/icons2/icon--plus.svg?component";
import { TextField2 } from "@/components/UI2/molecules/TextField";
import TrashIcon from "@/assets/images/icons2/icon--trash.svg?component";

import { AcceptedAgeRangeFormActions } from "./AcceptedAgeRangeFormActions";
import { ageRangeSchema, type AgeRangeFormValues } from "./schema";
import { FORM_ID } from "./constants";

type AcceptedAgeRangeFormProps = {
  acceptedAgeRanges: AgeRange[];
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (ranges: AgeRange[]) => void;
};

export function AcceptedAgeRangeForm({
  acceptedAgeRanges,
  isOpen,
  onCancel,
  onSubmit,
}: AcceptedAgeRangeFormProps) {
  const { control, formState, handleSubmit, reset } = useForm<AgeRangeFormValues>({
    defaultValues: { ranges: acceptedAgeRanges },
    mode: "onChange",
    resolver: zodResolver(ageRangeSchema),
  });

  const { append, fields, remove } = useFieldArray({ control, name: "ranges" });

  // Re-sync form to saved state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      reset({ ranges: acceptedAgeRanges });
    }
  }, [isOpen, acceptedAgeRanges, reset]);

  return (
    <>
      <form
        className="tw:flex tw:flex-col tw:gap-4"
        data-public
        id={FORM_ID}
        onSubmit={handleSubmit((v) => onSubmit(v.ranges))}
      >
        {fields.map((field, index) => (
          <>
            {index !== 0 && <Divider2 />}

            <div className="tw:grid tw:grid-cols-[1fr_1fr_44px] tw:gap-3" key={field.id}>
              <Controller
                control={control}
                name={`ranges.${index}.min`}
                render={({ field: f, fieldState }) => (
                  <TextField2
                    errorMessage={fieldState.error?.message}
                    hasStrongLabel
                    isInvalid={!!fieldState.error}
                    isRequired
                    label="Minimum Age"
                    mode="input"
                    onChange={(val) => f.onChange(val === "" ? NaN : Number(val))}
                    placeholder="None"
                    textInputProps={{
                      ...getAutomationDataAttr("AcceptedAgeRangeFormField_min"),
                      max: 200,
                      min: 5,
                      type: "number",
                    }}
                    value={Number.isFinite(f.value) ? String(f.value) : ""}
                  />
                )}
              />

              <Controller
                control={control}
                name={`ranges.${index}.max`}
                render={({ field: f, fieldState }) => (
                  <TextField2
                    errorMessage={fieldState.error?.message}
                    hasStrongLabel
                    isInvalid={!!fieldState.error}
                    isRequired
                    label="Maximum Age"
                    mode="input"
                    onChange={(val) => f.onChange(val === "" ? NaN : Number(val))}
                    placeholder="None"
                    textInputProps={{
                      ...getAutomationDataAttr("AcceptedAgeRangeFormField_max"),
                      max: 200,
                      min: 5,
                      type: "number",
                    }}
                    value={Number.isFinite(f.value) ? String(f.value) : ""}
                  />
                )}
              />

              {index !== 0 && (
                <Button2
                  aria-label="Remove age range"
                  className="tw:ml-auto tw:size-11 tw:self-end"
                  onClick={() => remove(index)}
                  rawChildren
                  type="button"
                  variant="blank"
                >
                  <TrashIcon className="tw:size-5 tw:text-fill-error-primary" />
                </Button2>
              )}
            </div>
          </>
        ))}

        <Button2
          className="tw:flex tw:w-fit tw:items-center tw:justify-center tw:gap-1.5"
          onPress={() => append({ max: NaN, min: NaN })}
          rawChildren
          type="button"
          variant="link"
        >
          <PlusIcon className="tw:size-4" />
          Add Age Range
        </Button2>
      </form>

      <AcceptedAgeRangeFormActions isDisabled={!formState.isValid} onCancel={onCancel} />
    </>
  );
}
